/**
 * LE NŒUD — l'écran de lecture, 80 % du temps de jeu.
 *
 * DEUX TEMPS QUI NE SE MÉLANGENT PAS (design §3) : le CONTEXTE, qu'on lit et qui défile, et la
 * DÉCISION, qu'on prend et qui ne bouge pas. C'est la structure du livre-jeu depuis toujours — le
 * paragraphe, puis « si vous ouvrez la porte, rendez-vous au 214 » — et la reliure la rend visible.
 *
 * LE TEXTE SE LIT EN FEUILLETS. 320 mots font quatre écrans sur un téléphone, et des choix qu'on
 * atteint après un voyage sont des choix qu'on prend mal. Le découpage est pur et testé
 * (`lecture/feuillets.ts`) ; il coupe entre les paragraphes, jamais dedans.
 *
 * LES CHOIX RESTENT ATTEIGNABLES DEPUIS N'IMPORTE QUEL FEUILLET. On ne verrouille pas la décision
 * derrière la lecture : ce serait forcer à relire un nœud déjà connu, et transformer une commodité
 * en corvée. La pagination sert la lecture, elle ne la surveille pas.
 *
 * LES CHOIX SE DISTINGUENT PAR LEUR FORME, PAS PAR LEUR COULEUR — un encadré, une gouttière, et
 * leurs coûts inscrits en marge. Un choix verrouillé RESTE LISIBLE, désaturé d'un cran, avec ce qui
 * manque écrit en toutes lettres : on ne cache pas une porte parce qu'on n'a pas la clef.
 */
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import type { ChoixEnVue, LesTraces, NoeudEnVue, ObjetConnu, PartieEnVue } from "../../api/types.js";
import { Effigie } from "../../composants/Effigie.js";
import { Glyphe } from "../../composants/Glyphe.js";
import { Illustration } from "../../composants/Illustration.js";
import { Lettrine } from "../../composants/Lettrine.js";
import { Paysage } from "../../composants/Paysage.js";
import { Traces } from "../../composants/Traces.js";
import { Voix } from "../../composants/Voix.js";
import { aLireAHauteVoix, amorceDe, feuilletsDe, mesureDeFeuillet, numeroDeFeuillet } from "../../lecture/feuillets.js";
import { echelleDe, useTailleDuTexte } from "../../lecture/taille.js";
import { CARACTERISTIQUES, ETATS, dureeDite, nomDObjet } from "../../lexique/mots.js";
import { LaFouille } from "./Fouille.js";
import { t, tn } from "../../lexique/langue.js";

/**
 * CE QU'UN RATÉ COÛTE, EN UNE PHRASE. Le serveur dit la NATURE de l'échec, jamais sa formule ; on
 * l'écrit donc en toutes lettres et sans chiffre. « Agilité 13 » dit la chance qu'on a ; « un raté
 * vous coûtera du sang » dit ce qu'on risque — et un permadeath se joue sur le second.
 */
function ceQuUnRateCoute(echec: NonNullable<ChoixEnVue["echec"]>): string {
  const morceaux: string[] = [];
  if (echec.blesse) morceaux.push(t("vous blesse"));
  if (echec.etat) morceaux.push(ETATS[echec.etat].verbe);
  if (echec.ailleurs) {
    morceaux.push(echec.lieu ? t("vous jette vers {lieu}", { lieu: echec.lieu }) : t("vous jette plus loin"));
  }
  if (morceaux.length === 0) return t("Un raté ne vous coûte que du temps");
  // « blesse, met en sang et jette plus loin » : la dernière conséquence s'annonce par « et ». Une
  // énumération à virgules jusqu'au bout se lit comme une liste de courses, pas comme un risque.
  const dernier = morceaux.pop()!;
  const suite = morceaux.length ? t("{debut} et {fin}", { debut: morceaux.join(", "), fin: dernier }) : dernier;
  return t("Un raté {suite}", { suite });
}

/**
 * LE SEUIL (§3.2) — ce qui attend, avant qu'on l'engage.
 *
 * Le combat s'ouvrait dans le geste de l'arrivée : on passait d'un choix à des jets de dés sans
 * avoir lu une ligne du nœud. Ici, le texte se lit, la bête se montre, et l'on décide. Ce qu'on
 * gagne n'est pas une échappatoire — les chemins restent fermés tant qu'on n'en a pas découdu —,
 * c'est le temps de boire, de s'équiper, de regarder son sac.
 *
 * L'EFFIGIE PLUTÔT QU'UN NOM, comme dans le combat : on ne se prépare pas à affronter une chaîne de
 * caractères. Une illustration posée passe devant, toujours (§6).
 */
function LeSeuil({
  rencontre,
  bloque,
  surEngage
}: {
  rencontre: NonNullable<PartieEnVue["rencontre"]>;
  bloque: boolean;
  surEngage: () => void;
}) {
  const combien = rencontre.adversaires.reduce((total, a) => total + a.quantite, 0);
  return (
    <div className="seuil">
      <p className="seuil__annonce capitales">
        <Glyphe nom="danger" taille={14} />{" "}
        {tn(combien, "Un adversaire vous barre le passage", "{n} adversaires vous barrent le passage")}
      </p>

      <ul className="seuil__bandeau">
        {rencontre.adversaires.map((a) => (
          <li key={a.espece}>
            {a.illustration ? (
              <Illustration url={a.illustration} legende={a.nom} forme="portrait" />
            ) : (
              <Effigie signalement={{ espece: a.espece, nom: a.nom }} taille={72} />
            )}
            <span className="seuil__nom">
              {a.nom}
              {a.quantite > 1 ? <span className="nombre faible"> {t("×")}{a.quantite}</span> : null}
            </span>
          </li>
        ))}
      </ul>

      <button type="button" className="bouton bouton--engage" disabled={bloque} onClick={surEngage}>
        {t("En découdre")}
      </button>
      {/* CE QUE LE SEUIL SERT À FAIRE. Sans cette ligne, un joueur clique « En découdre » par
          réflexe et découvre après coup qu'il pouvait boire — ce qui est précisément le geste que
          ce chantier existe pour rendre possible. */}
      <p className="faible seuil__conseil">
        {t("Le sac et le corps restent ouverts : c'est le moment de boire, de s'équiper. Fouiller, dormir et partir attendront.")}
      </p>
    </div>
  );
}

function Choix({
  choix,
  catalogue,
  surChoix,
  bloque,
  retenu = false
}: {
  choix: ChoixEnVue;
  catalogue: Map<string, ObjetConnu>;
  surChoix: (id: string) => void;
  bloque: boolean;
  /** RETENU PAR UNE RENCONTRE. Le choix reste LISIBLE — désaturé d'un cran, jamais éteint (design
   * §3) : on pèse où l'on ira après, pendant qu'on décide comment aborder ce qui est là. */
  retenu?: boolean;
}) {
  const verrouille = !choix.ouvert || retenu;
  return (
    <button
      type="button"
      className={verrouille ? "choix choix--verrouille" : "choix"}
      disabled={verrouille || bloque}
      onClick={() => (retenu ? undefined : surChoix(choix.id))}
      aria-disabled={verrouille}
    >
      <span className="choix__texte">{choix.label}</span>

      <span className="choix__mentions">
        {/* OÙ ÇA MÈNE. Le nom du lieu voisin est un panneau, pas le contenu du village (brief,
            « Décisions prises ») — et sans lui, six sorties se ressemblent toutes. */}
        {choix.vers?.lieu ? (
          <span className="mention mention--vers">
            <Glyphe nom="carte" taille={14} />{" "}
            {choix.vers.changeDActe
              ? t("vers {lieu} — on n'en revient pas de sitôt", { lieu: choix.vers.lieu })
              : t("vers {lieu}", { lieu: choix.vers.lieu })}
          </span>
        ) : choix.vers?.surPlace ? (
          <span className="mention mention--vers">
            <Glyphe nom="revisiter" taille={14} /> {t("sur place")}
          </span>
        ) : null}

        {choix.jet ? (
          <span className="mention mention--jet">
            <Glyphe nom="des" taille={14} /> {CARACTERISTIQUES[choix.jet.stat].nom} {choix.jet.dc}
          </span>
        ) : null}

        {choix.coutHeures > 0 ? (
          <span className="mention mention--temps">
            <Glyphe nom="horloge" taille={14} /> {dureeDite(choix.coutHeures)}
          </span>
        ) : null}

        {choix.exigeUnObjet ? (
          <span className={verrouille ? "mention mention--verrou" : "mention"}>
            <Glyphe nom="verrou" taille={14} /> {nomDObjet(choix.exigeUnObjet, catalogue)}
          </span>
        ) : null}

        {verrouille && choix.verrou === "quete_incomplete" ? (
          <span className="mention mention--verrou">
            <Glyphe nom="verrou" taille={14} /> {t("Il vous manque ce qu'il faut avoir fait avant")}
          </span>
        ) : null}

        {/* CE QU'UN ÉCHEC COÛTE. Seulement là où il y a un jet : ailleurs, il n'y a pas d'échec, et
            l'annoncer ferait craindre un dé qui n'existe pas. */}
        {choix.jet && choix.echec ? (
          <span className="mention mention--echec">
            <Glyphe nom="danger" taille={14} /> {ceQuUnRateCoute(choix.echec)}
          </span>
        ) : null}
      </span>
    </button>
  );
}

/**
 * LA MESURE D'UN FEUILLET POUR LA FENÊTRE COURANTE, ET POUR LA TAILLE CHOISIE. On s'abonne au
 * redimensionnement plutôt que de le lire une fois : on tourne un téléphone, on réduit une fenêtre,
 * et le découpage suit. Il suit aussi le réglage de taille du texte (design §5) — sans quoi le cran
 * « immense » redonnerait des feuillets de trois écrans, c'est-à-dire le mur de texte qu'on a
 * défait. Le calcul lui-même reste pur : ici on ne fait que lui donner trois nombres.
 *
 * L'instantané est une CHAÎNE et non un objet : `useSyncExternalStore` compare par identité, et un
 * `{largeur, hauteur}` frais à chaque appel ferait boucler le rendu indéfiniment.
 */
function useMesureDeFeuillet(): number {
  const [cran] = useTailleDuTexte();
  const fenetre = useSyncExternalStore(
    (rappel) => {
      window.addEventListener("resize", rappel);
      return () => window.removeEventListener("resize", rappel);
    },
    () => `${window.innerWidth}x${window.innerHeight}`,
    () => "1024x800"
  );
  const [largeur, hauteur] = fenetre.split("x").map(Number);
  return mesureDeFeuillet(largeur ?? 1024, hauteur ?? 800, echelleDe(cran));
}

export function Noeud({
  noeud,
  catalogue,
  surChoix,
  bloque,
  partieId,
  nuit = false,
  rencontre = null,
  traces = null,
  surEngage,
  surFouille,
  surPrends,
  surFouilleLesLieux
}: {
  noeud: NoeudEnVue;
  catalogue: Map<string, ObjetConnu>;
  surChoix: (id: string) => void;
  bloque: boolean;
  /** De quoi demander la bande du §7 au serveur. Absent, on lit à la voix du navigateur. */
  partieId?: string;
  /** L'horloge sait qu'il fait nuit (§3.4) : la vue du lieu s'assombrit, sans quoi elle montrerait
   * un plein jour pendant qu'on avance à la torche. */
  nuit?: boolean;
  /** CE QUI ATTEND ICI (§3.2), tant qu'on ne l'a pas engagé. Presque toujours `null`. */
  rencontre?: PartieEnVue["rencontre"];
  /** CE QUE LES AUTRES ONT LAISSÉ ICI (§Q21). Il arrive après le nœud, par une requête à part : une
   * trace ne change rien à l'état du jeu, et le texte se lit sans l'attendre. */
  traces?: LesTraces | null;
  surEngage: () => void;
  surFouille: (cacheId: string, reponse?: string) => void;
  surPrends: (cacheId: string, objetId: string) => void;
  surFouilleLesLieux: () => void;
}) {
  const mesure = useMesureDeFeuillet();
  const feuillets = useMemo(() => feuilletsDe(noeud.texte, mesure), [noeud.texte, mesure]);
  const amorce = useMemo(() => amorceDe(noeud.texte), [noeud.texte]);
  const [page, poseLaPage] = useState(0);
  const colonne = useRef<HTMLDivElement>(null);

  // UN NŒUD QUI S'OUVRE S'OUVRE À SON PREMIER FEUILLET, EN HAUT. Sans ce retour, on arriverait au
  // milieu du texte précédent, et le §design le dit : c'est un nœud qu'on croit avoir déjà lu.
  useEffect(() => {
    poseLaPage(0);
    colonne.current?.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
  }, [noeud.clef]);

  const courant = feuillets[Math.min(page, feuillets.length - 1)] ?? [];
  const plusieurs = feuillets.length > 1;
  const dernier = page >= feuillets.length - 1;

  // IL RESTE À LIRE, OU NON (design §3). Le découpage vise une hauteur d'écran sans jamais la
  // mesurer — c'est ce qui le rend prouvable —, et il déborde donc parfois d'une ligne ou deux.
  // C'est la SEULE hauteur qu'on lise dans tout l'écran de lecture, et elle ne décide d'aucun
  // découpage : elle allume une estompe, rien de plus. Une phrase tranchée net au ras d'une barre
  // se lit comme un défaut d'affichage ; une phrase qui s'estompe se lit comme une suite.
  const [reste, poseLeReste] = useState(false);
  useEffect(() => {
    const cadre = colonne.current;
    if (!cadre) return;
    const regarde = () => poseLeReste(cadre.scrollTop + cadre.clientHeight < cadre.scrollHeight - 2);
    regarde();
    cadre.addEventListener("scroll", regarde, { passive: true });
    const observateur = new ResizeObserver(regarde);
    observateur.observe(cadre);
    return () => {
      cadre.removeEventListener("scroll", regarde);
      observateur.disconnect();
    };
  }, [feuillets, page, noeud.clef]);


  const tourne = (vers: number) => {
    poseLaPage(vers);
    colonne.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <article className="noeud">
      <div className={reste ? "noeud__page noeud__page--suite" : "noeud__page"} ref={colonne}>
        {/* LE BANDEAU. L'illustration posée passe devant ; à défaut — c'est le cas ordinaire —, le
            lieu se dessine (design §3). Ce n'est pas un pis-aller : trois cents nœuds sans jamais
            voir où l'on est, et la carte cesse d'être un lieu pour devenir un tableau. */}
        {noeud.illustration ? (
          <Illustration url={noeud.illustration} legende={noeud.titre} forme="bandeau" />
        ) : (
          <figure className="vignette vignette--bandeau vignette--dessinee">
            <Paysage biome={noeud.biome} lieu={noeud.lieu} nuit={nuit} />
          </figure>
        )}

        <header className="noeud__entete">
          <div className="noeud__mentions">
            <div className="rang rang--serre capitales">
              <span>{t("Acte {n}", { n: noeud.acte })}</span>
              {noeud.ancre ? (
                <span className="rang rang--serre" title={t("On revient ici en cas de reprise (mode Chronique)")}>
                  <Glyphe nom="ancre" taille={14} /> {t("Ancre")}
                </span>
              ) : null}
              {/* LE LIEU DEMANDE DE LA LUMIÈRE, ET L'ON Y EST SANS FLAMME (§3.8). La mention
                  existait déjà et ne disait rien de plus qu'un fait ; elle dit maintenant CE QUE
                  ÇA COÛTE, ici et maintenant — un chiffre qu'on peut peser avant de prendre un
                  chemin. Éclairé, elle rappelle simplement que le lieu est noir : c'est ce qui
                  justifie la flamme qui brûle. */}
              {noeud.lumiereRequise ? (
                <span
                  className={noeud.obscurite ? "rang rang--serre mention--verrou" : "rang rang--serre"}
                  title={
                    noeud.obscurite
                      ? t(
                          "À tâtons : {malus} à tous les jets, une heure de plus pour avancer, et l'on ne trouve rien en fouillant",
                          { malus: noeud.obscurite.tousJets }
                        )
                      : t("Ce lieu est noir : votre flamme y sert à quelque chose")
                  }
                >
                  <Glyphe nom="nuit" taille={14} />{" "}
                  {noeud.obscurite
                    ? t("Dans le noir · {malus} à tous les jets", { malus: noeud.obscurite.tousJets })
                    : t("Éclairé")}
                </span>
              ) : null}
            </div>
            <Voix
              texte={aLireAHauteVoix(noeud.titre, courant)}
              lecture={
                partieId
                  ? { partieId, clef: noeud.clef, feuillets, amorce, page, surPage: tourne }
                  : undefined
              }
            />
          </div>
          <h1 className="noeud__titre">{noeud.titre}</h1>
        </header>

        <div className="noeud__corps">
          {courant.map((paragraphe, index) =>
            page === 0 && index === 0 ? (
              <Lettrine key={`${page}-${index}`} texte={paragraphe} />
            ) : (
              <p className="noeud__texte" key={`${page}-${index}`}>
                {paragraphe}
              </p>
            )
          )}
        </div>

      </div>

      {/* LE PIED DE LA PAGE. Il est HORS de la zone qui défile : collé au bas du texte, il finissait
          par recouvrir les lignes qu'il surplombait, et un bouton posé sur une phrase qu'on est en
          train de lire est un bouton qu'on clique par accident. Il ne se montre que lorsqu'il sert :
          un « i / i » est une pagination qui ment sur le travail qu'elle fait. */}
      {plusieurs ? (
        <nav className="feuillets" aria-label={t("Feuillets de ce nœud")}>
          <button type="button" className="puce puce--sourde" disabled={page === 0} onClick={() => tourne(page - 1)}>
            <Glyphe nom="revers" taille={13} /> {t("Revenir")}
          </button>
          <span className="feuillets__rang capitales" aria-live="polite">
            ❧ {numeroDeFeuillet(page)} / {numeroDeFeuillet(feuillets.length - 1)}
          </span>
          <button type="button" className="puce" disabled={dernier} onClick={() => tourne(page + 1)}>
            {t("Suivre")} <Glyphe nom="suite" taille={13} />
          </button>
        </nav>
      ) : null}

      {/* LA DÉCISION. Elle ne défile pas avec le texte : c'est sa place, et elle ne change pas d'un
          nœud à l'autre. L'amorce s'y répète pour qu'on puisse choisir sans remonter.
          L'ORDRE EST L'AMORCE, LES CHOIX, PUIS CE QUE LE LIEU RECÈLE. Le seuil fait exception, et
          c'est la seule : quand une rencontre attend, tous les choix sont retenus, et le seul geste
          vivant se met en tête. */}
      <div className="decision">
        {amorce ? <p className="decision__amorce">{amorce}</p> : null}
        {/* LE SEUIL PASSE DEVANT LA FOUILLE. On ne vide pas un coffre avec un loup en face, et la
            règle est tenue par le serveur : ce qui coûte du temps est refusé tant qu'on n'a pas
            tranché. Autant ne pas montrer le geste plutôt que de le montrer et le refuser. */}
        {rencontre ? <LeSeuil rencontre={rencontre} bloque={bloque} surEngage={surEngage} /> : null}
        <div className="choix-liste">
          {noeud.choix.map((choix) => (
            <Choix
              key={choix.id}
              choix={choix}
              catalogue={catalogue}
              surChoix={surChoix}
              bloque={bloque}
              retenu={rencontre !== null}
            />
          ))}
          {!noeud.choix.length ? (
            <p className="douce">{t("Il n'y a plus de chemin d'ici. C'est une fin.")}</p>
          ) : null}
        </div>
        {/* CE QUE LE LIEU RECÈLE VIENT APRÈS LES CHOIX (design §3), et l'ordre n'est pas cosmétique :
            passée devant eux, la fouille les repoussait hors de l'écran, et l'on lisait à la place de
            la décision un encadré qui lui ressemblait. */}
        {rencontre ? null : (
          <LaFouille
            noeud={noeud}
            catalogue={catalogue}
            bloque={bloque}
            surFouille={surFouille}
            surPrends={surPrends}
            surFouilleLesLieux={surFouilleLesLieux}
          />
        )}
        {/* LES TRACES FERMENT LA DÉCISION (design §3). Elles viennent APRÈS tout ce qui se décide :
            ce que d'autres ont vécu ici éclaire un choix, mais ne doit jamais se présenter avant
            lui — et surtout jamais entre les choix, où on les cliquerait par erreur. */}
        <Traces traces={traces} />
      </div>
    </article>
  );
}
